import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardIssue } from "../schema/dashboard";

/** Structurally an `IssueDisplayProps`; kept apart so this module does not import its caller. */
export interface ZohoSearchFieldIssueDisplay {
	title: string;
	description: string;
	primaryActionLabel: string;
	primaryActionTo: string;
}

interface RefusedField {
	module: string;
	field: string;
	label: string | null;
}

const fieldsOf = (issue: DashboardIssue): RefusedField[] => {
	const fields = issue.payload?.fields;

	if (!Array.isArray(fields)) {
		return [];
	}

	return fields.flatMap((entry: unknown) => {
		const { module, field, label } = (entry ?? {}) as Record<string, unknown>;

		if (typeof module !== "string" || typeof field !== "string") {
			return [];
		}

		return [{ module, field, label: typeof label === "string" && label ? label : null }];
	});
};

const describeField = ({ module, field, label }: RefusedField): string =>
	`"${label ?? field}" on ${module}`;

/**
 * The card for a mapped LinkedIn field Zoho refuses to search (PREC-4354). Duplicate detection
 * then matches on email and name only, so the card names the field and the change that fixes it,
 * which is made in Zoho. The card goes away once Zoho answers a search on the field.
 */
export function zohoSearchFieldIssueDisplay(issue: DashboardIssue): ZohoSearchFieldIssueDisplay {
	const fields = fieldsOf(issue);
	const named = fields.length > 0 ? fields.map(describeField).join(" and ") : "the field";
	const plural = fields.length > 1;

	return {
		title: "Zoho can't search your LinkedIn field",
		description: `Zoho refuses to search ${named}, where your LinkedIn URLs are mapped, so Signalist can't find people already in Zoho by their LinkedIn profile and may create a second record for them. Make ${plural ? "them single-line text fields" : "it a single-line text field"} in Zoho, or map the LinkedIn URL onto one that is.`,
		primaryActionLabel: "Review field mapping",
		primaryActionTo: `${ROUTE_CONFIG.INTEGRATIONS_ZOHO}?zohoTab=CONNECTION`,
	};
}
